import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { Pencil, Plus, Settings2, Tags, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { errorMessage } from '@/api/errors'
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormField } from '@/components/forms/FormField'
import { Button, IconButton } from '@/components/ui/Button'
import { Card, CardBody, CardFooter, CardHeader } from '@/components/ui/Card'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Input } from '@/components/ui/Input'
import { PageHeader } from '@/components/ui/PageHeader'
import { Can } from '@/features/auth/guards'
import { useAuth } from '@/features/auth/useAuth'
import {
  categoriesQuery,
  librarySettingsQuery,
  useDeleteCategory,
  useSaveSettings,
} from '@/features/library/api'
import { CategoryDialog } from '@/features/library/components/CategoryDialog'
import { settingsSchema, type SettingsFormInput, type SettingsFormOutput } from '@/features/library/schemas'
import type { Category, LibrarySettings } from '@/features/library/types'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

const FIELDS = Object.keys(settingsSchema.shape)

function RulesForm({ settings }: { settings: LibrarySettings }) {
  const { t } = useTranslation('library')
  const { can } = useAuth()
  const save = useSaveSettings()
  const readOnly = !can('library.manage')
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting, isDirty },
    reset,
  } = useForm<SettingsFormInput, unknown, SettingsFormOutput>({
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      loanDays: String(settings.loanDays),
      maxActiveLoansStudent: String(settings.maxActiveLoansStudent),
      maxActiveLoansStaff: String(settings.maxActiveLoansStaff),
      finePerDay: settings.finePerDay,
    },
  })

  const onSubmit = handleSubmit(async (values) => {
    try {
      const saved = await save.mutateAsync(values)
      reset({
        loanDays: String(saved.loanDays),
        maxActiveLoansStudent: String(saved.maxActiveLoansStudent),
        maxActiveLoansStaff: String(saved.maxActiveLoansStaff),
        finePerDay: saved.finePerDay,
      })
      toast.success(t('Library rules saved'))
    } catch (e) {
      applyServerErrors(e, setError, FIELDS)
    }
  })

  return (
    <Card>
      <CardHeader
        title={t('Circulation rules')}
        description={t('Apply to new loans and to fines recorded at return.')}
        icon={<Settings2 />}
      />
      <form onSubmit={onSubmit} noValidate>
        <fieldset disabled={readOnly}>
          <CardBody className="grid gap-4 sm:grid-cols-2">
            <FormField
              label={t('Loan period (days)')}
              error={errors.loanDays?.message}
              required
              hint={t('Also the length of a renewal.')}
            >
              <Input type="number" min={1} max={365} inputMode="numeric" {...register('loanDays')} />
            </FormField>
            <FormField
              label={t('Fine per day late ({{currency}})', { currency: settings.currency })}
              error={errors.finePerDay?.message}
              required
              hint={t('Currency comes from the school profile.')}
            >
              <Input type="number" min={0} step="0.01" inputMode="decimal" {...register('finePerDay')} />
            </FormField>
            <FormField
              label={t('Max books per student')}
              error={errors.maxActiveLoansStudent?.message}
              required
            >
              <Input
                type="number"
                min={1}
                max={50}
                inputMode="numeric"
                {...register('maxActiveLoansStudent')}
              />
            </FormField>
            <FormField
              label={t('Max books per staff member')}
              error={errors.maxActiveLoansStaff?.message}
              required
            >
              <Input
                type="number"
                min={1}
                max={50}
                inputMode="numeric"
                {...register('maxActiveLoansStaff')}
              />
            </FormField>
          </CardBody>
        </fieldset>
        <Can permission="library.manage">
          <CardFooter>
            <Button type="submit" loading={isSubmitting} disabled={!isDirty}>
              {t('Save rules')}
            </Button>
          </CardFooter>
        </Can>
      </form>
    </Card>
  )
}

function CategoriesCard() {
  const { t } = useTranslation('library')
  const { data, isPending, error, refetch } = useQuery(categoriesQuery())
  const remove = useDeleteCategory()
  const [editing, setEditing] = useState<Category | 'new' | null>(null)
  const [deleting, setDeleting] = useState<Category | null>(null)

  const onDelete = async (c: Category) => {
    try {
      await remove.mutateAsync(c.id)
      toast.success(t('Category deleted'), c.name)
    } catch (e) {
      toast.error(t('Could not delete'), errorMessage(e))
    } finally {
      setDeleting(null)
    }
  }

  return (
    <Card>
      <CardHeader
        title={t('Categories')}
        description={t('Group the catalogue for browsing and filters.')}
        icon={<Tags />}
        actions={
          <Can permission="library.manage">
            <Button size="sm" variant="soft" leftIcon={<Plus />} onClick={() => setEditing('new')}>
              {t('Add')}
            </Button>
          </Can>
        }
      />
      {isPending ? (
        <LoadingState className="min-h-32" />
      ) : error ? (
        <ErrorState onRetry={() => void refetch()} compact />
      ) : data.length === 0 ? (
        <EmptyState icon={<Tags />} title={t('No categories yet')} compact />
      ) : (
        <ul className="divide-y divide-line">
          {data.map((c) => (
            <li key={c.id} className="flex items-center gap-3 px-5 py-3">
              <span className="min-w-0 flex-1 truncate font-medium text-ink">{c.name}</span>
              <span className="text-xs text-muted tabular">
                {t('{{count}} books', { count: c.bookCount })}
              </span>
              <Can permission="library.manage">
                <IconButton
                  size="icon-sm"
                  label={t('Rename {{name}}', { name: c.name })}
                  icon={<Pencil />}
                  onClick={() => setEditing(c)}
                />
                <IconButton
                  size="icon-sm"
                  label={t('Delete {{name}}', { name: c.name })}
                  icon={<Trash2 />}
                  onClick={() => setDeleting(c)}
                  disabled={c.bookCount > 0}
                />
              </Can>
            </li>
          ))}
        </ul>
      )}
      {editing ? (
        <CategoryDialog
          open
          onOpenChange={(o) => (o ? undefined : setEditing(null))}
          category={editing === 'new' ? undefined : editing}
        />
      ) : null}
      {deleting ? (
        <ConfirmDialog
          open
          onOpenChange={(o) => (o ? undefined : setDeleting(null))}
          title={t('Delete “{{name}}”?', { name: deleting.name })}
          description={t('Only unused categories can be deleted.')}
          confirmLabel={t('Delete')}
          loading={remove.isPending}
          onConfirm={() => void onDelete(deleting)}
        />
      ) : null}
    </Card>
  )
}

export function LibrarySettingsPage() {
  const { t } = useTranslation('library')
  useDocumentTitle('Library settings')
  const settings = useQuery(librarySettingsQuery())
  return (
    <>
      <PageHeader
        title={t('Library settings')}
        description={t('Loan period, borrowing limits, fines and catalogue categories.')}
        breadcrumbs={[{ label: t('Library'), to: '/admin/library' }, { label: t('Settings') }]}
        homeHref="/admin"
      />
      <div className="grid gap-6 lg:grid-cols-2">
        {settings.isPending ? (
          <LoadingState />
        ) : settings.error ? (
          <ErrorState onRetry={() => void settings.refetch()} />
        ) : (
          <RulesForm settings={settings.data} />
        )}
        <CategoriesCard />
      </div>
    </>
  )
}
